import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Beer, MapPin, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import IconBadge from '../IconBadge';
import { GET_STARTED_CHOICES, currentProps } from './navItems';
import { HEADER_PANEL, HeaderAction } from './headerParts';
import { focusables, onPanelKeyDown } from './menuKeys';
import useDismiss from './useDismiss';

const ICONS = { business: Store, venue: Beer, guest: MapPin };
const TONES = { business: 'butter', venue: 'peach', guest: 'sage' };

const LABEL_SIZES = {
  // the desktop popover
  sm: 'text-[1.0625rem] tracking-[-0.02em]',
  // the mobile Menu panel
  md: 'text-lg tracking-[-0.025em]',
};

/**
 * The three audience rows (GET_STARTED_CHOICES): a square pastel icon tile, the label
 * (Manrope 500, as the account rows) and one line of text (DM Sans 14px, muted). Each row is
 * one link, at least 44px tall; the current page's row carries aria-current. A list named by
 * `aria-label` ("Get started" by default) or by `labelledBy` (the id of a visible label).
 * `onNavigate` runs on every row (closes the panel it sits in). Used by GetStartedMenu (the
 * desktop and narrow-screen popover) and at the top of MobileMenu.
 */
export function ChooserList({
  pathname, onNavigate, labelledBy, size = 'sm', className,
}) {
  return (
    <ul
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': 'Get started' })}
      className={cn('m-0 list-none p-0', className)}
    >
      {GET_STARTED_CHOICES.map((choice, i) => {
        const Icon = ICONS[choice.key] || MapPin;
        return (
          <li key={choice.key} className={cn(i > 0 && 'border-t border-brand-rule')}>
            <Link
              to={choice.to}
              onClick={onNavigate}
              className={cn(
                'group/row flex min-h-11 items-center gap-3.5 py-3 text-navy no-underline',
                'transition-colors duration-180 ease-out hover:bg-brand-paper',
                size === 'sm' ? 'px-4' : '-mx-2 px-2',
              )}
              {...currentProps(pathname, choice.to)}
            >
              <IconBadge tone={TONES[choice.key] || 'sage'} size="sm" shape="square">
                <Icon strokeWidth={1.75} />
              </IconBadge>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block font-numeral font-medium leading-snug underline-offset-4 decoration-1',
                    'group-hover/row:underline group-aria-[current=page]/row:underline',
                    LABEL_SIZES[size] || LABEL_SIZES.sm,
                  )}
                >
                  {choice.label}
                </span>
                <span className="mt-0.5 block font-body text-sm leading-snug text-brand-muted">{choice.text}</span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The header's "Get started →" (brief §2.1): a disclosure button that opens a small panel
 * under the bar with the three audience rows (ChooserList): Advertise my business →
 * /for-businesses, Get free coasters for my venue → /for-venues, Find deals near me →
 * /local-deals-near-me. Shown at every width (the bar keeps the button below 800px too;
 * there the panel spans the bar, from 800px it hangs right-aligned under the button). The
 * mobile Menu panel carries the same rows at its top.
 *
 * Keyboard: Enter/Space toggles and focus stays on the button; Tab moves on through the
 * rows; ArrowDown on the button opens and focuses the first row; ArrowUp/ArrowDown/Home/End
 * move within the panel; Escape closes and returns focus to the button; Tab past the last
 * row, focus leaving, a click outside, or following a row closes it (useDismiss).
 *
 * Props: `pathname` (marks the current row), `open` / `onOpenChange` (state lives in
 * SiteHeader, so a navigation or the 800px switch closes it).
 */
export default function GetStartedMenu({ pathname, open, onOpenChange }) {
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = `site-start-${useId().replace(/:/g, '')}`;
  const [focusFirst, setFocusFirst] = useState(false);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDismiss(open, close, wrapRef, buttonRef);

  useEffect(() => {
    if (!open || !focusFirst) return;
    setFocusFirst(false);
    const [first] = focusables(panelRef.current);
    if (first) first.focus();
  }, [open, focusFirst]);

  const onButtonKeyDown = (e) => {
    if (e.key !== 'ArrowDown') return;
    e.preventDefault();
    setFocusFirst(true);
    onOpenChange(true);
  };

  return (
    // Not positioned: the panel hangs from the bar (the fixed header), like the account menu.
    <div ref={wrapRef} className="flex min-w-0 shrink items-center">
      <HeaderAction
        ref={buttonRef}
        open={open}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onOpenChange(!open)}
        onKeyDown={onButtonKeyDown}
      >
        Get started
      </HeaderAction>
      <div
        id={panelId}
        ref={panelRef}
        hidden={!open}
        onKeyDown={onPanelKeyDown}
        data-get-started-panel=""
        className={cn(
          HEADER_PANEL,
          'absolute left-[-1px] right-[-1px] top-full whitespace-normal py-1.5',
          '[@media(min-width:50em)]:left-auto [@media(min-width:50em)]:w-[23.5rem] [@media(min-width:50em)]:max-w-[calc(100vw-2.5rem)]',
        )}
      >
        <ChooserList pathname={pathname} onNavigate={close} size="sm" />
      </div>
    </div>
  );
}
